export const WORLDS_ROUTE = "/worlds";
export const WORLDS_DRAFT_STORAGE_KEY = "stc-worlds-draft-v1";

export const PAID_ACTIONS = [
  { id: "primary-character", label: "Primary character sales", shortLabel: "Character sale" },
  { id: "primary-land", label: "Primary land sales", shortLabel: "Land sale" },
  { id: "primary-item", label: "Primary item sales", shortLabel: "Item sale" },
  { id: "upgrade", label: "Paid upgrades", shortLabel: "Upgrade" },
  { id: "construction-save", label: "Construction or state saves", shortLabel: "State save" },
  { id: "evolution", label: "Character or item evolutions", shortLabel: "Evolution" },
  { id: "crafting", label: "Crafting payments", shortLabel: "Crafting" },
  { id: "marketplace-resale", label: "Marketplace resales", shortLabel: "Resale" },
  { id: "game-entry", label: "Paid game or event entries", shortLabel: "Event entry" },
] as const;

export type PaidActionId = typeof PAID_ACTIONS[number]["id"];
export type CollectionPlan = "existing" | "new-waves" | "hybrid";
export type InscriptionPlan = "immutable-only" | "authorised-children";
export type LandModel = "plots" | "zones" | "open-world";
export type ResourceModel = "none" | "rune" | "points" | "hybrid";

export interface WorldsDraft {
  version: 1;
  project: {
    name: string;
    creator: string;
    collectionPlan: CollectionPlan;
    collectionReference: string;
    inscriptionPlan: InscriptionPlan;
  };
  world: {
    premise: string;
    landEnabled: boolean;
    landModel: LandModel;
    landSupply: string;
    resourceModel: ResourceModel;
    resourceName: string;
    materials: string;
    productionLoop: string;
  };
  play: {
    craftingEnabled: boolean;
    upgradesEnabled: boolean;
    gamesEnabled: boolean;
    craftingNotes: string;
    gameNotes: string;
    contentCadence: string;
    wavePlan: string;
  };
  economics: {
    pricingNotes: string;
    paidActions: Record<PaidActionId, boolean>;
  };
}

export interface StoredWorldsDraft {
  savedAt: string;
  draft: WorldsDraft;
}

const DEFAULT_PAID_ACTIONS: Record<PaidActionId, boolean> = {
  "primary-character": true,
  "primary-land": true,
  "primary-item": true,
  upgrade: true,
  "construction-save": true,
  evolution: true,
  crafting: true,
  "marketplace-resale": true,
  "game-entry": true,
};

export function createWorldsDraft(): WorldsDraft {
  return {
    version: 1,
    project: {
      name: "",
      creator: "",
      collectionPlan: "existing",
      collectionReference: "",
      inscriptionPlan: "authorised-children",
    },
    world: {
      premise: "",
      landEnabled: true,
      landModel: "plots",
      landSupply: "",
      resourceModel: "rune",
      resourceName: "",
      materials: "",
      productionLoop: "",
    },
    play: {
      craftingEnabled: true,
      upgradesEnabled: true,
      gamesEnabled: true,
      craftingNotes: "",
      gameNotes: "",
      contentCadence: "Quarterly content waves",
      wavePlan: "",
    },
    economics: {
      pricingNotes: "",
      paidActions: { ...DEFAULT_PAID_ACTIONS },
    },
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isWorldsDraft(value: unknown): value is WorldsDraft {
  if (!isRecord(value) || value.version !== 1) return false;
  if (!isRecord(value.project) || !isRecord(value.world) || !isRecord(value.play) || !isRecord(value.economics)) return false;
  if (typeof value.project.name !== "string" || typeof value.project.creator !== "string") return false;
  if (typeof value.world.premise !== "string" || typeof value.world.landEnabled !== "boolean") return false;
  if (typeof value.play.craftingEnabled !== "boolean" || typeof value.play.gamesEnabled !== "boolean") return false;
  const paidActions = value.economics.paidActions;
  if (!isRecord(paidActions)) return false;
  return PAID_ACTIONS.every(({ id }) => typeof paidActions[id] === "boolean");
}

export function readWorldsDraft(storage: Pick<Storage, "getItem">): StoredWorldsDraft | null {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(WORLDS_DRAFT_STORAGE_KEY) ?? "null");
    if (!isRecord(parsed) || typeof parsed.savedAt !== "string" || !isWorldsDraft(parsed.draft)) return null;
    return parsed as unknown as StoredWorldsDraft;
  } catch {
    return null;
  }
}

export function saveWorldsDraft(
  storage: Pick<Storage, "setItem">,
  draft: WorldsDraft,
  savedAt = new Date().toISOString(),
): StoredWorldsDraft {
  const stored = { savedAt, draft } satisfies StoredWorldsDraft;
  storage.setItem(WORLDS_DRAFT_STORAGE_KEY, JSON.stringify(stored));
  return stored;
}

export function enabledPaidActions(draft: WorldsDraft): typeof PAID_ACTIONS[number][] {
  return PAID_ACTIONS.filter(({ id }) => draft.economics.paidActions[id]);
}

export function draftCompletion(draft: WorldsDraft): number {
  const checks = [
    Boolean(draft.project.name.trim()),
    Boolean(draft.project.creator.trim()),
    Boolean(draft.project.collectionReference.trim()) || draft.project.collectionPlan === "new-waves",
    Boolean(draft.world.premise.trim()),
    !draft.world.landEnabled || Boolean(draft.world.landSupply.trim()),
    draft.world.resourceModel === "none" || Boolean(draft.world.resourceName.trim()),
    Boolean(draft.world.materials.trim()),
    Boolean(draft.world.productionLoop.trim()),
    Boolean(draft.play.craftingNotes.trim()),
    !draft.play.gamesEnabled || Boolean(draft.play.gameNotes.trim()),
    Boolean(draft.play.wavePlan.trim()),
    Boolean(draft.economics.pricingNotes.trim()),
    enabledPaidActions(draft).length > 0,
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}
